// Spec 45 component-behavior — cmp_ds_popover.
//
// Non-modal floating surface anchored to a trigger. Distinguishes
// from Modal:
//   * NO focus trap — user can Tab through the popover content but
//     also Tab back out into the page.
//   * NO body scroll lock — page remains scrollable.
//   * Closes on Escape AND on outside click.
//   * Initial focus moves into the popover (first focusable) so
//     keyboard users land where they expect.
//
// Positioning: this Phase 4b.5 ships a SIMPLE absolute-positioned
// popover (top + left props from caller). Auto-positioning + collision
// avoidance via floating-ui lands in a future Adım when the surface
// stabilises. Until then, callers compute placement themselves OR
// wrap with their own positioning helper.

import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";
import { Portal } from "../lib/portal.tsx";

export interface PopoverProps extends Omit<HTMLAttributes<HTMLDivElement>, "role"> {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly children?: ReactNode;
  /** Optional accessible label for the popover surface (consumer-driven). */
  readonly "aria-label"?: string;
  /** Override Escape-dismissal — defaults true. */
  readonly closeOnEscape?: boolean;
  /** Override outside-click-dismissal — defaults true. */
  readonly closeOnOutsideClick?: boolean;
}

export const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      open,
      onClose,
      "aria-label": ariaLabel,
      closeOnEscape = true,
      closeOnOutsideClick = true,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const setRef = useCallback(
      (node: HTMLDivElement | null) => {
        popoverRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref !== null) ref.current = node;
      },
      [ref],
    );

    // Initial focus: first focusable inside the popover (or the popover
    // itself if it has no focusables). setTimeout(0) over queueMicrotask
    // because microtasks fire before React's commit in happy-dom test
    // environments — the focusables query runs against a stale tree.
    useEffect(() => {
      if (!open) return;
      const node = popoverRef.current;
      if (node === null) return;
      const timer = setTimeout(() => {
        const first = node.querySelector<HTMLElement>(
          "a[href], button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex='-1'])",
        );
        (first ?? node).focus();
      }, 0);
      return () => clearTimeout(timer);
    }, [open]);

    // Outside-click handler.
    useEffect(() => {
      if (!open || !closeOnOutsideClick) return;
      const handler = (event: globalThis.MouseEvent) => {
        const node = popoverRef.current;
        if (node === null) return;
        if (!node.contains(event.target as Node)) {
          onClose();
        }
      };
      // Defer one tick so the click that OPENED the popover doesn't
      // immediately close it.
      const timeout = setTimeout(() => {
        document.addEventListener("mousedown", handler);
      }, 0);
      return () => {
        clearTimeout(timeout);
        document.removeEventListener("mousedown", handler);
      };
    }, [open, closeOnOutsideClick, onClose]);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (closeOnEscape && event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };

    if (!open) return null;

    return (
      <Portal>
        {/* role="dialog" makes this an interactive element per ARIA,
            but the lint rule's notion of "non-interactive" is HTML-
            tag-driven and doesn't account for role. */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <div
          ref={setRef}
          role="dialog"
          aria-label={ariaLabel}
          tabIndex={-1}
          data-component="popover"
          className={cx("nai-popover", className)}
          onKeyDown={handleKeyDown}
          {...rest}
        >
          {children}
        </div>
      </Portal>
    );
  },
);
Popover.displayName = "Popover";
